/**
 * useChainSession — 5-minute BSC deposit-scan session (IBO deposit-monitor style).
 *
 * Used on Deposit UI and History. Activates on mount, ends on unmount / expiry.
 * When DEPOSIT_POLL_SESSION_GATED=true, the poller only scans while this is active.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const POLL_MS = 5000;

export default function useChainSession() {
  const [session, setSession] = useState({
    active: false,
    secondsRemaining: 0,
    ttl: 300,
    expired: false,
  });
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/api/wallet/chain/status");
      const sess = data?.session || {};
      if (mountedRef.current) {
        setSession((prev) => ({
          active: !!sess.active,
          secondsRemaining: sess.seconds_remaining ?? 0,
          ttl: sess.ttl ?? 300,
          expired: prev.expired || (!sess.active && prev.active),
        }));
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const activate = async () => {
      try {
        const { data } = await api.post("/api/wallet/chain/session");
        const sess = data?.session || {};
        if (mountedRef.current) {
          setSession({
            active: !!sess.active,
            secondsRemaining: sess.seconds_remaining ?? 0,
            ttl: sess.ttl ?? 300,
            expired: false,
          });
        }
      } catch {
        /* ignore */
      }
    };

    activate();
    intervalRef.current = setInterval(fetchStatus, POLL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
      api.delete("/api/wallet/chain/session").catch(() => {});
    };
  }, [fetchStatus]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.post("/api/wallet/chain/session");
      const sess = data?.session || {};
      setSession({
        active: !!sess.active,
        secondsRemaining: sess.seconds_remaining ?? 0,
        ttl: sess.ttl ?? 300,
        expired: false,
      });
    } catch {
      /* ignore */
    }
  }, []);

  return { ...session, refresh };
}
